/**
 * MC Player Profile — over-time per-day delta chart (LEAD-05, D-06, D-08, D-15).
 *
 * Initializes on /mc/players/<uuid> only.
 *
 * ⚠ THE GUARD IS ON THE METRIC SELECT, NOT ON THE CHART CONTAINER — and that is load-bearing.
 * player.latte renders #mc-profile-chart ONLY when the server-side series reports has_data, and on
 * prod today every real player has exactly one daily snapshot, so has_data is false for all of them
 * and the container is ABSENT from every real profile view (228-08-SUMMARY, "For Plan 09"). Guarding
 * on the container would make this module dead code on the only pages it ships to; assuming the
 * container exists would throw on every profile view. The controls, by contrast, render
 * unconditionally — so they are the honest presence marker, and the container is created on demand.
 *
 * ⚠ THE UUID IS NOT ALWAYS READABLE FROM THE CONTAINER either, for the same reason. It is read from
 * the container's data attribute when the container exists, and from the route path otherwise.
 *
 * D-06: a counter reset floors the bar to zero height. A zero-height bar alone makes a
 * data-destroying event look like a quiet day, so every reset date ALSO gets a vertical xAxis
 * plotLine labelled "Counter reset". Both, never just the bar.
 *
 * D-07: dates are Toronto calendar days, bucketed at write time. Every Y-m-d below is parsed into
 * LOCAL date components rather than through Date.parse, which would read a bare Y-m-d as midnight
 * UTC and shift every bar onto the previous day in Toronto. The global time option that makes
 * Highcharts render in local time is set once in src/index.ts and is deliberately not overridden
 * per-chart here.
 *
 * A NULL POINT IS A GAP, NOT A ZERO. The model emits a point only for days it can actually diff, so
 * absent days simply have no column. A gap day's single bar carries the whole gain since the last
 * recorded snapshot and says so in its tooltip via span_days — it is never spread or interpolated.
 *
 * ── Phase 239 plan 10 — D-09 ─────────────────────────────────────────────────────────────────────
 * Binds #mc-profile-server-select (THIS page's own DOM marker id, deliberately distinct from
 * leaderboard's #mc-leaderboard-server-select, feed's #mc-server-select and heatmap's
 * #mc-heatmap-server-select, so a shared module cannot double-bind).
 *
 * BOTH signal URLs below carry the picker's server. `?do=getPlayerSessions` used to hardcode the
 * fleet-wide value even though the backend handler has always accepted the parameter; `?do=
 * getPlayerChart` had no parameter at all until Plan 07 Task 3 added one server-side. That second
 * one matters most: without it the chart silently reverts to fleet-wide the instant a viewer
 * touches the metric or window controls while a server is selected — a first-paint-correct /
 * AJAX-wrong divergence, the exact class handleGetLeaderboard()'s own D-14 comment block warns
 * about, and the class RESEARCH Pitfall 5 named for this page.
 *
 * The selection lives in exactly two places: the <select> element and the URL. NOTHING is persisted
 * — not client-side, not server-side. This repo has a documented bug class where persisted
 * dashboard query params let one visitor (or a probe) clobber another's state.
 *
 * CR-04 (239-REVIEW) amended HOW the server picker writes the URL, not WHERE the selection lives:
 * it now navigates to the scoped URL instead of rewriting the address bar in place. It has to —
 * six of this page's blocks are server-rendered from one $profile payload with no refresh signal,
 * so an in-place rewrite left them at the previous server's grain (D-09's rejected outcome). The
 * metric and window controls are unaffected and still refresh the chart in place; they change what
 * a fetchable block shows, not what the server computed the whole page over.
 *
 * ⚠ Those three absent API names are enforced by LITERAL grep, so none of them is spelled out
 * above: a rule written verbatim in a comment fails its own check. Same discipline as
 * leaderboard.ts's syncUrl() and leaderboard.latte's header.
 */

import * as Highcharts from 'highcharts';
// Phase 232 (FEED-05, D-13): the session-history timeline's ONLY new module import. Side-effect
// import mirrors the existing timeline/heatmap/drilldown/highcharts-more precedent in this repo
// (tracking.ts, lastfm.ts, stardew.ts, mc/resourceHistory.ts) — same loud-guard idiom below, same
// getChartTheme() merge. Ships inside the already-installed highcharts@12 — no new package.
import 'highcharts/modules/xrange';
import { getChartTheme } from '../utils/chartTheme';
// Phase 240 (240-06, D-05/SC2): the one app-wide "absolute instant — relative age" header helper,
// used ONLY by the session-timeline tooltip below. The daily-change bar chart further down is
// deliberately NOT a consumer (D-06) — its x-value is a whole calendar day, so a relative age
// against it would be fabricated precision.
import { chartInstantHeader } from '../utils/instants';
// Phase 246 (246-05, D-04/D-05/D-06/D-07): axis-bounds, column-width-cap and inactivity-note
// logic extracted into a browser-free module so `node` can pin it directly — see
// profileChartMeta.ts's own header.
import { dataAxisBounds, inactivityNoteText, PROFILE_COLUMN_MAX_POINT_WIDTH } from '../utils/profileChartMeta';
// Phase 272 (SURF-02, D-10/D-11): the shared death-forensics panel builder — the SAME module
// feed.ts's stream-row expand chevron uses, so the two surfaces can never drift out of sync on
// what a death's forensics body looks like.
import { renderInto } from './forensicsPanel';

// ---------------------------------------------------------------------------
// Payload types — McAnalyticsModel::getOverTimeSeries()
// ---------------------------------------------------------------------------

/**
 * Phase 273 D-06/D-17 (Plan 12): `value` is nullable — 273-05 wired `getOverTimeSeries()` to emit a
 * TRUE NULL (never a zero, never an omitted point — dates/span_days are always kept) whenever
 * `active_playtime` cannot be diffed for that day: a pre-rollout reading (D-06/AFK-06) or a
 * fleet-ineligible unscoped series (D-13/D-17's all-or-nothing rule). Every OTHER metric this
 * catalog can select has always produced a real `float` here (a day the model cannot diff at all is
 * simply OMITTED from `points[]`, per this file's own "A NULL POINT IS A GAP, NOT A ZERO" header
 * note above) — this type was `number` before this plan, which was already stale the moment 273-05
 * shipped; widening it here is what makes `renderProfileChart()`'s own `y: point.value` (below)
 * type-honest rather than merely running-by-luck.
 */
export interface SeriesPoint {
    date: string;
    value: number | null;
    reset: boolean;
    span_days: number;
}

interface SeriesMetric {
    key: string;
    label: string;
    unit: string;
    scope: string;
    windowable: boolean;
}

interface SeriesPayload {
    uuid: string;
    metric: SeriesMetric | null;
    unit: string | null;
    window: string;
    points: SeriesPoint[];
    has_data: boolean;
    empty_reason: string | null;
    empty_kind: string | null;
    reset_dates: string[];
    biggest_day: { date: string; value: number } | null;
    gap_days: number;
    rows_in_window: number;
    window_label: string;
    // Plan 246-03 (D-06): the server's own Toronto calendar day, present on every
    // decorateWindow()-routed payload. Nullable so a stale cached response (pre-246-03) cannot
    // produce a runtime type lie — see profileChartMeta.ts's own note-builder's null-argument
    // handling.
    today: string | null;
}

// ---------------------------------------------------------------------------
// Payload types — McFeedModel::getPlayerSessions() (FEED-05, D-13/D-14)
// ---------------------------------------------------------------------------

interface SessionRow {
    joined_at_ms: number;
    left_at_ms: number | null;
    /** One of the three D-14 states: 'completed' | 'ongoing' | 'ended-uncleanly'. */
    state: string;
    session_seconds: number | null;
    server_key: string;
    server_name: string;
}

interface SessionsPayload {
    has_data: boolean;
    sessions: SessionRow[];
}

// ---------------------------------------------------------------------------
// Fixed data-identity colours
//
// These are DATA IDENTITY, not chrome, so they are fixed hex rather than theme tokens — and
// deliberately NOT the runtime-customizable accent. A series colour that changed when the viewer
// picked a different accent would imply the accent carries meaning on this chart, which it does not.
// ---------------------------------------------------------------------------

/** Matches the first entry of the existing server-colour palette used across the MC section. */
const SERIES_COLOR = '#2196F3';

/** Biggest-day highlight — distinct from both the series blue and the accent (228-UI-SPEC.md). */
const BIGGEST_DAY_COLOR = '#FFB300';

/** The cumulative overlay, muted so it reads as secondary to the delta columns it summarises. */
const CUMULATIVE_COLOR = '#9C27B0';

const RESET_TOOLTIP_COPY =
    'Counter reset detected — raw value was negative; showing 0 for this day.';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/**
 * The live chart instance. Named distinctly from every rendering function in this module: a
 * function-versus-module-variable name collision has bitten this repo before.
 */
let profileChart: Highcharts.Chart | null = null;

let playerUuid = '';
let currentMetric = '';
let currentWindow = '';
let showCumulative = false;

/**
 * D-09/Phase 249 (D-09/D-10/D-11): the picker's checked server keys, threaded into BOTH signal
 * URLs in this module.
 *
 * Defaults to the empty D-10 "all servers" list so every code path below has a valid value even on
 * a page that somehow renders without the picker — the parameter is never omitted, only ever
 * widened.
 */
let currentServers: string[] = [];

/** The most recent payload, kept so the cumulative toggle can rebuild without a second round trip. */
let lastPayload: SeriesPayload | null = null;

let listenersBound = false;
let requestSeq = 0;

/** The live session-timeline xrange chart instance, named distinctly from `profileChart` above. */
let sessionChart: Highcharts.Chart | null = null;
let sessionRequestSeq = 0;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function initProfile(): void {
    const select = document.getElementById('mc-profile-metric-select') as HTMLSelectElement | null;
    if (!select) return; // Only runs on /mc/players/<uuid>

    playerUuid = resolveUuid();
    if (playerUuid === '') return; // no subject to chart — refuse rather than fetch nonsense

    currentMetric = select.value || select.dataset.metric || '';
    currentWindow = readActiveWindow();

    // D-09/Phase 249: the client half of the ?server[]= deep link detail.latte, default.latte and
    // the leaderboard rows now emit (plus the legacy singular ?server= form, so an old bookmark
    // still pre-selects). player.latte already pre-selected the RESOLVED keys server-side, so this
    // only has to agree with it; the query-string read below is what makes a deep link that arrived
    // from a cached/shared URL still line up.
    //
    // ⚠ THE OPTION-EXISTENCE CHECK IS A UX AFFORDANCE, NOT THE SECURITY CONTROL. resolveScopedServerIds()'s
    // allow-list re-validates every `server[]` key on every request that reaches the server. Each
    // value is checked individually against CSS.escape'd attribute selector, copying feed.ts's
    // shipped pattern, so a crafted `server[]` value cannot break the query (T-239-V5-04). A value
    // with no matching option is skipped rather than echoed back.
    const serverSelect = document.getElementById('mc-profile-server-select') as HTMLSelectElement | null;
    if (serverSelect) {
        const params = new URLSearchParams(window.location.search);
        const deepLinkServers = [...params.getAll('server[]'), ...params.getAll('server')];
        for (const deepLinkServer of deepLinkServers) {
            const option = serverSelect.querySelector(`option[value="${CSS.escape(deepLinkServer)}"]`);
            if (option) (option as HTMLOptionElement).selected = true;
        }

        currentServers = Array.from(serverSelect.selectedOptions).map((o) => o.value);
    }

    if (!listenersBound) {
        listenersBound = true;

        // Inside the guard with every other listener in this module: a shared component
        // initialised on every page and binding twice is a documented failure class here, and a
        // double-bound picker would fire two concurrent chart AND two concurrent session fetches
        // per change (T-239-BIND-01).
        if (serverSelect) {
            serverSelect.addEventListener('change', () => {
                // CR-04 (239-REVIEW): NAVIGATE — do not re-fetch in place.
                //
                // The chart (§3) and the session timeline (§4) are the ONLY two blocks on this page
                // that can re-fetch after first paint. Everything else — the headline stat tiles,
                // advancement progress, the per-server breakdown, Position & Exploration, Inventory
                // & Experience, and the "All stats" dump — is server-rendered from the single
                // $profile payload and has no signal to refresh through. Refreshing only the two
                // fetchable blocks therefore left the MAJORITY of the page at the previous scope:
                // a <select> reading "Horror" sitting above fleet-wide playtime/kills/deaths totals
                // and fleet-wide per-server cards. That is D-09's rejected outcome inverted.
                //
                // D-11 made $server a real action parameter on actionPlayer() precisely so one
                // navigation re-renders every block at one grain, and that only pays off if the
                // picker actually navigates. Nothing is persisted anywhere by this — the URL stays
                // the sole carrier of the selection, exactly as this module's header requires.
                navigateToServer(Array.from(serverSelect.selectedOptions).map((o) => o.value));
            });
        }

        select.addEventListener('change', () => {
            currentMetric = select.value;
            void fetchSeries();
        });

        document
            .querySelectorAll<HTMLElement>('#mc-profile-windows .mc-range-btn')
            .forEach((btn) => {
                btn.addEventListener('click', () => {
                    const key = btn.dataset.window;
                    if (!key) return;

                    currentWindow = key;

                    document
                        .querySelectorAll('#mc-profile-windows .mc-range-btn')
                        .forEach((b) => {
                            b.classList.remove('btn-primary');
                            b.classList.add('btn-outline-secondary');
                        });
                    btn.classList.remove('btn-outline-secondary');
                    btn.classList.add('btn-primary');

                    void fetchSeries();
                });
            });

        buildCumulativeToggle();
    }

    // Fetch on init ONLY when the server actually rendered a chart box to fill. When it did not,
    // the first paint is already the correct NAMED empty card built from the same payload this
    // module would fetch — re-requesting it would cost a round trip to redraw what is on screen.
    if (document.getElementById('mc-profile-chart')) {
        void fetchSeries();
    }

    initSessionTimeline();
    initGravesAccordion();
}

// ---------------------------------------------------------------------------
// Graves accordion — lazy forensics body fill on first expand (SURF-02, D-10/D-11)
//
// DELIBERATE DIVERGENCE from skills.latte's/quests.latte's own accordions, which render their
// entire body server-side and eagerly (272-RESEARCH.md Pattern 3): a single real death can carry
// ~47 item objects with nested enchant arrays, so player.latte's §4.5 Graves section (Plan
// 272-10) ships each accordion item's body as a deliberately EMPTY `[data-death-id]` shell — D-11
// requires flag-eager (the row already renders unconditionally), body-lazy (the item lists/XP/
// badge/damage-type fill only once a viewer actually opens that specific grave). This function is
// the lazy-fill half, reusing the SAME `forensicsPanel.ts` module `feed.ts`'s chevron uses so the
// two surfaces' rendered body can never drift apart.
//
// Bootstrap's own `data-bs-toggle="collapse"` markup on each `.accordion-button` already supplies
// `aria-expanded` toggling natively (confirmed against the rendered `player.latte` markup: every
// button carries `aria-expanded="false"`/`aria-controls` and Bootstrap's data-api manages it from
// there) — unlike feed.ts's hand-built chevron, this function does zero manual ARIA wiring.
// ---------------------------------------------------------------------------

function initGravesAccordion(): void {
    const container = document.getElementById('mc-graves-accordion');
    if (!container) return; // no graves on this profile (or zero graves on the selected servers)

    // 272-UI-SPEC.md's Component Reuse Contract names no bespoke class for this section, and this
    // plan's own files_modified is TS-only (player.latte is 272-10's file, not this plan's) — so
    // the `.mc-graves-section` marker the 320px overflow probe locates the accordion through is
    // added here, at runtime, on the section ancestor Plan 272-10 already rendered with the
    // `.mc-profile-section` class. `classList.add()` is naturally idempotent — a second call (or
    // a second `initGravesAccordion()` invocation) can never add a duplicate token.
    container.closest<HTMLElement>('.mc-profile-section')?.classList.add('mc-graves-section');

    container.querySelectorAll<HTMLElement>('.accordion-collapse').forEach((collapseEl) => {
        // Idempotency guard (the standing shared-component double-bind rule,
        // feedback_shared_component_every_page_init_double_bind) — per-element, so a defensive
        // re-call to initGravesAccordion() can never double-bind the same accordion item's
        // show.bs.collapse listener.
        if (collapseEl.dataset.gravesBound === 'true') return;
        collapseEl.dataset.gravesBound = 'true';

        collapseEl.addEventListener('show.bs.collapse', () => {
            const body = collapseEl.querySelector<HTMLElement>('[data-death-id]');
            if (!body) return;

            // D-11: a second expand of the same grave must not re-render. forensicsPanel.ts's own
            // per-id response cache already means a second `renderInto()` call issues no second
            // network request, but calling it again here would still replace an already-filled
            // body with a brief "Loading forensics…" flash for no reason — this flag is the
            // minimal guard needed to avoid exactly that, nothing more.
            if (body.dataset.forensicsLoaded === 'true') return;
            body.dataset.forensicsLoaded = 'true';

            const deathId = body.dataset.deathId;
            if (!deathId) return; // defensive — player.latte always sets this attribute

            void renderInto(body, deathId);
        });
    });
}

// ---------------------------------------------------------------------------
// Session-history timeline (FEED-05, D-13/D-14)
//
// Lane-A-event-sourced (`session.join`/`session.quit` via `mc_events`), deliberately DISTINCT
// from the log-parser `mc_player_sessions` table shown on /mc/analytics — a different source
// answering a different question ("when did the mod see this player online", not the log
// parser's own reconstruction).
//
// D-13 de-risk (non-skippable per the UI-SPEC): before committing to this as the shipped path, a
// real `xrange` render was prototyped at a 320px viewport with all three D-14 states present in
// one fixture — a completed bar, an "ongoing, extending to now" open bar, and an ended-uncleanly
// bar. Per-point `color` overrides and an open-ended `x2` bar pinned to the current instant both
// render exactly as documented (Highcharts' own xrange API surface, RESEARCH.md Assumption A3 —
// now resolved): this module ships the `xrange` render, not the named flex-row fallback.
//
// UNLIKE the daily-change chart above, THE GUARD IS ON THE SESSION-TIMELINE'S OWN CONTAINER ID —
// never `#mc-profile-metric-select` — because player.latte renders `#mc-profile-session-timeline`
// UNCONDITIONALLY (it carries no server-side has_data computation to gate on; this section runs no
// query in actionPlayer()). That container is therefore always present and is the correct,
// permanent presence marker for this section, the same role the metric-select plays for the chart.
// ---------------------------------------------------------------------------

function initSessionTimeline(): void {
    const container = document.getElementById('mc-profile-session-timeline');
    if (!container) return; // its OWN container id — this section has no other reliable marker

    const uuid = resolveSessionUuid(container);
    if (uuid === '') return; // no subject to fetch — refuse rather than fetch nonsense

    void fetchSessions(uuid);
}

/**
 * D-09: a picker change re-runs the same resolve-then-fetch path first paint used, so the timeline
 * can never be left showing the previous server's sessions. Deliberately re-reads the container's
 * own data attribute rather than caching a uuid: the container is the section's only reliable
 * marker, and one lookup per change is free.
 */
function refetchSessions(): void {
    initSessionTimeline();
}

async function fetchSessions(uuid: string): Promise<void> {
    const seq = ++sessionRequestSeq;

    // D-09's first concrete change: this parameter used to be the hardcoded fleet-wide sentinel
    // even though handleGetPlayerSessions() has always accepted a scope (Phase 249: read via
    // readServerKeys(), no declared parameter) — the hardcode was the only thing standing between
    // the picker and a scoped timeline. One `server[]=` term per checked key, mirroring
    // fetchSeries()'s identical build.
    const sessionServerTerms = currentServers
        .map((v) => `&server[]=${encodeURIComponent(v)}`)
        .join('');
    const url = `?do=getPlayerSessions&uuid=${encodeURIComponent(uuid)}`
        + sessionServerTerms;

    try {
        const resp = await fetch(url, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!resp.ok) {
            console.error('[mc-profile] Session timeline fetch failed:', resp.status);
            if (seq === sessionRequestSeq) showSessionTimelineError();
            return;
        }

        const payload: SessionsPayload = await resp.json();
        if (seq !== sessionRequestSeq) return; // a newer request has already been issued

        renderSessionTimeline(payload);
    } catch (err) {
        console.error('[mc-profile] Session timeline fetch error:', err);
        if (seq === sessionRequestSeq) showSessionTimelineError();
    }
}

function renderSessionTimeline(payload: SessionsPayload): void {
    // On has_data:false the server-rendered empty state ("No session history yet") is the correct
    // view, and not one word of its locked UI-SPEC §7.8 copy is rewritten here — the reset below
    // is VISIBILITY ONLY.
    //
    // The reset itself is what Phase 239 adds: on first paint it is a no-op (those elements are
    // already in exactly that state), but after a picker change a previous server's timeline may
    // still be on screen, and returning early would strand this one section at a stale grain —
    // precisely the "one block at a different grain than everything around it" outcome D-09
    // rejected.
    if (!payload.has_data || payload.sessions.length === 0) {
        destroySessionChart();
        resetSessionTimelineToEmpty();
        return;
    }

    // Loud guard: the module import above registers 'xrange' as a side effect. If it somehow
    // failed to register, fail loud rather than let Highcharts.chart() throw an opaque error.
    const seriesTypes = (Highcharts as unknown as { seriesTypes?: Record<string, unknown> }).seriesTypes;
    if (typeof seriesTypes?.['xrange'] === 'undefined') {
        console.error('[mc-profile] xrange module failed to register — session timeline will not render.');
        return;
    }

    const container = document.getElementById('mc-profile-session-timeline');
    if (!container) return;

    const legend = document.getElementById('mc-profile-session-legend');
    const empty = document.getElementById('mc-profile-session-empty');
    if (empty) empty.style.display = 'none';
    if (legend) legend.style.display = '';
    container.style.display = '';

    // D-14: three explicit colours read live from the --mc-feed-state-* custom properties Plan 05
    // authored in mc.scss FOR this exact consumer — each one itself aliases a --bs-info/-success/
    // -warning contextual var, never hardcoded hex and never the customizable --ksat-accent.
    const completedColor = readToken('--mc-feed-state-completed', '#0dcaf0');
    const ongoingColor = readToken('--mc-feed-state-ongoing', '#198754');
    const endedUncleanlyColor = readToken('--mc-feed-state-ended-uncleanly', '#ffc107');

    const stateColor = (state: string): string => {
        if (state === 'completed') return completedColor;
        if (state === 'ongoing') return ongoingColor;
        return endedUncleanlyColor; // 'ended-uncleanly'
    };

    const stateLabel = (state: string): string => {
        if (state === 'completed') return 'Completed';
        if (state === 'ongoing') return 'Ongoing';
        return 'Ended uncleanly';
    };

    const now = Date.now();

    const data: Highcharts.PointOptionsObject[] = payload.sessions.map((session) => {
        const x2 = session.state === 'ongoing' ? now : (session.left_at_ms ?? session.joined_at_ms);
        const custom = {
            state: session.state,
            serverName: session.server_name,
            sessionSeconds: session.session_seconds,
            joinedAtMs: session.joined_at_ms,
        };
        const point = {
            x: session.joined_at_ms,
            x2,
            y: 0,
            color: stateColor(session.state),
        } as Highcharts.PointOptionsObject & { custom: typeof custom };
        point.custom = custom;
        return point;
    });

    destroySessionChart();

    sessionChart = Highcharts.chart(container, Highcharts.merge(getChartTheme(), {
        chart: {
            type: 'xrange',
            inverted: window.innerWidth < 576, // mirrors tracking.ts's _initTimelineChart idiom
        },
        title: { text: '' },
        xAxis: { type: 'datetime' },
        yAxis: { categories: ['Sessions'], title: { text: '' } },
        legend: { enabled: false }, // the static HTML chip row above the container IS the legend
        tooltip: {
            // CHRT-03: pointFormatter ONLY — tooltip.formatter is FORBIDDEN.
            pointFormatter: function (this: Highcharts.Point): string {
                const custom = (this as Highcharts.Point & {
                    custom?: {
                        state?: string;
                        serverName?: string;
                        sessionSeconds?: number | null;
                        joinedAtMs?: number;
                    };
                }).custom ?? {};
                const state = custom.state ?? '';
                const serverName = custom.serverName ?? '';

                // Phase 240 (240-06, D-05/D-08/SC2): the join instant in the viewer's own timezone
                // plus its relative age, PREPENDED above the three existing lines — the chart stays
                // on pointFormatter (a single-point tooltip hits neither the shared nor the split
                // return-shape contract, so this is genuinely the minimal-diff path) and every one
                // of those three lines is left byte-identical below.
                //
                // D-08 provenance — do NOT convert this value. `joined_at_ms` is built server-side
                // by McFeedModel::occurredAtEpochMs() (app/Models/McFeedModel.php:939) as
                // `(new DateTime($naive . ' UTC'))->getTimestamp() * 1000` — the explicit ' UTC'
                // suffix makes it immune to PHP's runtime default timezone, the safer of the two
                // server-side patterns in this phase's audit. It is already a correct epoch-ms
                // number: pass it straight through. No re-parse, no manual offset, and no
                // per-chart browser-vs-UTC rendering-mode override (that flag is global in
                // src/index.ts and is deliberately never shadowed per-chart in this file).
                //
                // Absent is not zero (235 D-11 / 236 D-15): with no epoch there is NO header line
                // at all — not an em dash, not an "unknown" label. Machine-formatted Intl output
                // over a numeric epoch carries no wire-controlled substring, so unlike the three
                // lines below it needs no escaping — and none of theirs is removed.
                const header = custom.joinedAtMs != null
                    ? chartInstantHeader(custom.joinedAtMs)
                    : '';

                let durationLine: string;
                if (state === 'ended-uncleanly') {
                    // D-14, non-negotiable: NEVER a computed/guessed duration for this state.
                    durationLine = 'no clean disconnect';
                } else if (state === 'ongoing') {
                    // A live elapsed-so-far figure is honest here — it is real elapsed time since
                    // join, not a fabricated end. session_seconds itself stays null (D-14).
                    const elapsed = Math.max(0, Math.round((now - (custom.joinedAtMs ?? now)) / 1000));
                    durationLine = `ongoing — ${formatMetricValue(elapsed, 'duration')} so far`;
                } else {
                    durationLine = formatMetricValue(custom.sessionSeconds ?? 0, 'duration');
                }

                return (header !== '' ? `${header}<br/>` : '')
                    + `<b>${escapeHtml(stateLabel(state))}</b><br/>`
                    + `${escapeHtml(serverName)}<br/>`
                    + `${escapeHtml(durationLine)}`;
            },
        },
        series: [{
            type: 'xrange',
            name: 'Sessions',
            data,
            colorByPoint: false, // per-point `color` set explicitly above, not palette-cycled
        }],
        credits: { enabled: false },
    } as unknown as Highcharts.Options));
}

function destroySessionChart(): void {
    if (sessionChart) {
        sessionChart.destroy();
        sessionChart = null;
    }
}

/**
 * Puts the section back into the exact state player.latte renders it in: legend and timeline
 * hidden, the named empty card visible. Touches `style.display` and nothing else — every string in
 * that card stays the server-rendered one.
 */
function resetSessionTimelineToEmpty(): void {
    const container = document.getElementById('mc-profile-session-timeline');
    const legend = document.getElementById('mc-profile-session-legend');
    const empty = document.getElementById('mc-profile-session-empty');
    if (container) container.style.display = 'none';
    if (legend) legend.style.display = 'none';
    if (empty) empty.style.display = '';
}

/** Locked Copywriting Contract error copy (232-UI-SPEC.md), applied without touching the honest
 *  has_data:false empty state's own verbatim strings — this path only fires on a fetch failure. */
function showSessionTimelineError(): void {
    const empty = document.getElementById('mc-profile-session-empty');
    if (!empty) return;

    const heading = empty.querySelector<HTMLElement>('.chart-empty-title');
    if (heading) heading.textContent = "Couldn't load this session history";

    const state = empty.querySelector<HTMLElement>('.ksat-empty-state');
    if (state) {
        state.replaceChildren();
        const p = document.createElement('p');
        p.className = 'text-muted mb-0';
        p.textContent = 'The session history could not be refreshed just now. Reload the page to try again.';
        state.appendChild(p);
    }
}

/**
 * The container's OWN data attribute — unlike `resolveUuid()` above, this container is ALWAYS
 * rendered by player.latte (it carries no server-side has_data gate), so there is no route-path
 * fallback branch to maintain here.
 */
function resolveSessionUuid(container: HTMLElement): string {
    return container.dataset.uuid ?? '';
}

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

async function fetchSeries(): Promise<void> {
    const seq = ++requestSeq;

    // D-09 / RESEARCH Pitfall 5: the checked servers ride on EVERY re-fetch, not just the picker's
    // own. A metric or window change while one or more servers are selected goes through this same
    // function, so omitting the parameter here would have made the chart revert to fleet-wide the
    // moment a viewer touched an unrelated control — correct on first paint, wrong ever after. Plan
    // 249-07 widened the matching backend read (readServerKeys()); the two only work as a pair. One
    // `server[]=` term per checked key, no server term at all when the array is empty (D-10).
    const serverTerms = currentServers
        .map((v) => `&server[]=${encodeURIComponent(v)}`)
        .join('');
    const url = `?do=getPlayerChart&uuid=${encodeURIComponent(playerUuid)}`
        + `&metric=${encodeURIComponent(currentMetric)}`
        + `&window=${encodeURIComponent(currentWindow)}`
        + serverTerms;

    try {
        const resp = await fetch(url, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!resp.ok) {
            console.error('[mc-profile] Chart fetch failed:', resp.status);
            if (seq === requestSeq) {
                showEmptyCard(
                    'Couldn’t load this chart',
                    'The daily-change chart could not be refreshed just now. Reload the page to try again.',
                );
            }
            return;
        }

        const payload: SeriesPayload = await resp.json();
        if (seq !== requestSeq) return; // a newer request has already been issued

        lastPayload = payload;
        renderPayload(payload);
    } catch (err) {
        console.error('[mc-profile] Chart fetch error:', err);
        if (seq === requestSeq) {
            showEmptyCard(
                'Couldn’t load this chart',
                'The daily-change chart could not be refreshed just now. Reload the page to try again.',
            );
        }
    }
}

/**
 * CR-04 (239-REVIEW) — the picker's whole action: put the chosen scope in the URL and let the
 * server re-render the page at it.
 *
 * This replaced an in-place URL rewrite plus two AJAX refreshes. That shape could only reach the
 * two fetchable blocks and stranded the six server-rendered ones at the previous scope; see the
 * change listener above for the full list. `actionPlayer()` reads the checked keys through
 * readServerKeys() (D-11, widened Phase 249 D-09), so this one navigation brings back EVERY block
 * at the selected grain — which is the entire reason that read exists.
 *
 * Still shareable and refresh-durable, and still nothing written anywhere but the URL — see this
 * module's header for why the selection rides there and nowhere else, and why the three
 * deliberately-absent API names are not written out anywhere in this file.
 */
function navigateToServer(servers: string[]): void {
    const url = new URL(window.location.href);
    // Phase 249 (T-249-08-T2): BOTH the legacy scalar `server` key and the bracket `server[]` key
    // are deleted before appending, so a stale bookmarked scalar can never survive alongside the
    // new list and be read as an extra key server-side.
    url.searchParams.delete('server');
    url.searchParams.delete('server[]');
    for (const s of servers) {
        url.searchParams.append('server[]', s);
    }
    // A real navigation, so the viewer's back button returns to the previous scope's fully-rendered
    // page rather than to a page whose visible numbers never matched its own picker.
    window.location.assign(url.toString());
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

function renderPayload(payload: SeriesPayload): void {
    destroyChart();
    // Hoisted above the has_data branch: both branches need the identical call with the identical
    // argument, so hoisting removes the possibility of one branch drifting from the other.
    setChartMetaLine(payload);

    // D-15: fewer than two snapshots swaps in the NAMED empty card. No near-empty Highcharts
    // instance is constructed — a chart drawn over nothing is this repo's documented false-GREEN,
    // and a single zero-height bar would read as a measured zero rather than as absent history.
    if (!payload.has_data) {
        showEmptyCard(
            'Not enough history yet',
            'Over-time and biggest-day numbers need at least two daily snapshots to compute a '
            + 'difference — check back tomorrow.',
            payload.empty_reason,
        );
        setCumulativeToggleVisible(false);
        return;
    }

    hideEmptyCard();
    setCumulativeToggleVisible(true);
    renderProfileChart(payload);
}

/**
 * D-18 (Plan 12): builds the cumulative overlay's running-total VALUES (one per point, same order
 * as `points`) from the SAME per-day points already in hand — no extra API call.
 *
 * A null day (D-06/AFK-06's pre-rollout reading, or D-13/D-17's fleet-ineligible unscoped series)
 * BREAKS the running total (`null`) rather than coercing it to zero. The naive accumulator this
 * replaces added a day's raw value onto the running sum unconditionally — but `null` coerces to `0`
 * under JavaScript's `+`, so an unguarded add silently treated an untracked day as a MEASURED zero,
 * flatlining the cumulative line across EXACTLY the gap the primary column series correctly draws
 * as a visible break. That is the SC-2 "silently implies Active equals total playtime" failure,
 * merely hidden behind the `showCumulative` toggle rather than fixed.
 *
 * AFTER a gap, `running` RESUMES from its pre-gap total — it is NOT reset to `0`. This is the
 * deliberate, honest reading: the days inside the gap contributed an UNKNOWN (not a zero) amount,
 * so every cumulative value from that point on is a LOWER BOUND on the true total, never a false
 * "this player's pre-gap playtime never happened" claim — which restarting at 0 would assert, and
 * that is a LARGER lie than the gap itself.
 *
 * Extracted as its own pure, DOM-free, Highcharts-free function (unlike `renderProfileChart()`
 * itself, which also needs `parseLocalDate()`/`custom` point metadata this helper does not compute)
 * so the real, unmodified export can be exercised directly rather than re-typing the accumulator
 * logic into a test. `profile.ts`'s own `import 'highcharts/modules/xrange'` side effect requires a
 * real `window`/`globalThis` (confirmed: it throws under plain `node`), so this function's own
 * proof lives in `tools/probe-273-12-profile-cumulative.mjs` (a real headless-Chromium DOM, this
 * repo's other established host-runnable evidence class) rather than the plain-node
 * `tests/mc/*.test.ts` lane every DOM-free file in this phase uses.
 */
export function buildCumulativeValues(points: SeriesPoint[]): (number | null)[] {
    let running = 0;
    return points.map((point) => {
        const dayValue = point.value;
        if (dayValue === null) {
            return null;
        }
        running += dayValue;
        return running;
    });
}

function renderProfileChart(payload: SeriesPayload): void {
    const container = ensureChartContainer();
    const unit = payload.unit ?? 'count';
    const metricLabel = payload.metric === null ? 'Daily change' : payload.metric.label;

    // Resolved ONCE, here at construction time, exactly as getChartTheme() reads its own tokens. A
    // live custom-property reference left inside the options object never resolves — Highcharts
    // hands the string to a canvas/SVG attribute, not to the CSS cascade.
    const resetColor = readToken('--bs-danger', '#dc3545');

    const data: Highcharts.PointOptionsObject[] = payload.points.map((point) => {
        const isBiggest = payload.biggest_day !== null && point.date === payload.biggest_day.date;
        const entry: Highcharts.PointOptionsObject = {
            x: parseLocalDate(point.date),
            y: point.value,
        };
        // Never assign an undefined option value — build each key only when it applies.
        if (isBiggest) {
            entry.color = BIGGEST_DAY_COLOR;
            entry.dataLabels = {
                enabled: true,
                formatter: function (this: Highcharts.Point): string {
                    return formatMetricValue(this.y ?? 0, unit);
                },
            };
        }
        const custom = { dateLabel: point.date, reset: point.reset, spanDays: point.span_days };
        (entry as Highcharts.PointOptionsObject & { custom: typeof custom }).custom = custom;
        return entry;
    });

    // Plan 246-05 (D-04/D-05): bound the axis to the payload's OWN points, never to
    // window_start/window_end (window_end === today for four of the five windows, null for
    // all-time — reading it would reintroduce the "axis runs past the last real day" reading this
    // phase removes, and would make the fix behave differently per window). See
    // profileChartMeta.ts's own header for why the bound is computed from already-parsed epoch
    // milliseconds rather than re-parsing dates here.
    const axisBounds = dataAxisBounds(payload.points.map((point) => parseLocalDate(point.date)));

    // D-06: one dashed vertical marker per reset date. The floored bar renders at zero height; this
    // is what makes the discontinuity visible. The object shape is the one already proven at
    // src/js/oura.ts and src/js/stardew.ts — those are yAxis, this is the identical shape on xAxis.
    const resetLines: Highcharts.XAxisPlotLinesOptions[] = payload.reset_dates.map((date) => ({
        value: parseLocalDate(date),
        color: resetColor,
        width: 1,
        dashStyle: 'Dash' as Highcharts.DashStyleValue,
        zIndex: 5,
        label: {
            text: 'Counter reset',
            style: { color: resetColor, fontSize: '10px' },
        },
    }));

    const xAxisOptions: Highcharts.XAxisOptions = {
        type: 'datetime',
        plotLines: resetLines,
    };
    // Never assign an undefined option value (this file's own standing rule) — a NaN bound would
    // blank the whole chart, so min/max are set only when dataAxisBounds() found at least one
    // finite point.
    if (axisBounds !== null) {
        xAxisOptions.min = axisBounds.min;
        xAxisOptions.max = axisBounds.max;
    }

    const primarySeriesOptions = {
        type: 'column',
        name: metricLabel,
        data,
        color: SERIES_COLOR,
        yAxis: 0,
        // D-06 (Plan 12): MANDATORY — a pre-rollout/fleet-ineligible day's null point (widened onto
        // SeriesPoint.value above) draws a genuine visible break, never a line/bar to zero and never
        // an interpolation across the hole. The library default already matches; stated explicitly
        // anyway, matching this repo's own connectNulls convention (src/js/oura.ts,
        // src/js/mc/resourceHistory.ts). D-06 is deliberately minimal — nothing beyond the break
        // itself is drawn on the chart: "the gap in the series IS the disclosure," full stop.
        connectNulls: false,
    };
    // Highcharts' own SeriesColumnOptions type omits connectNulls even though the runtime option is
    // honoured on a column series (the option is real; only the narrowed TS shape is missing it) —
    // routed through an intermediate const (not a direct literal assignment) so this one property
    // does not trip the union member's excess-property check, matching this file's own established
    // "assert to Highcharts.Options at the merge boundary, not per-field" posture below.
    const series: Highcharts.SeriesOptionsType[] = [primarySeriesOptions as Highcharts.SeriesColumnOptions];

    const yAxes: Highcharts.YAxisOptions[] = [{
        min: 0,
        allowDecimals: unit !== 'count',
        title: { text: metricLabel },
    }];

    if (showCumulative) {
        // D-18 (Plan 12): running-summed from the SAME delta points already in hand — no extra API
        // call. Off by default; the chart is rebuilt on every toggle, so no stale series can survive
        // a re-toggle. buildCumulativeValues() null-gaps rather than coercing (see its own docblock
        // for the SC-2 failure this closes and why a post-gap resume, not a restart, is correct).
        const cumulativeValues = buildCumulativeValues(payload.points);
        const cumulative: Highcharts.PointOptionsObject[] = payload.points.map((point, i) => {
            const entry: Highcharts.PointOptionsObject = {
                x: parseLocalDate(point.date),
                y: cumulativeValues[i],
            };
            const custom = { dateLabel: point.date, reset: point.reset, spanDays: point.span_days };
            (entry as Highcharts.PointOptionsObject & { custom: typeof custom }).custom = custom;
            return entry;
        });

        yAxes.push({
            min: 0,
            allowDecimals: unit !== 'count',
            title: { text: `${metricLabel} (cumulative)` },
            opposite: true,
        });

        series.push({
            type: 'line',
            name: `${metricLabel} (cumulative)`,
            data: cumulative,
            color: CUMULATIVE_COLOR,
            yAxis: 1,
            marker: { enabled: false },
            // D-18: MANDATORY — the cumulative line must genuinely BREAK on a null day too, never
            // bridge the hole by drawing straight through it (which would visually imply a smooth
            // climb across a gap this file's own primary series correctly shows as absent).
            connectNulls: false,
        });
    }

    profileChart = Highcharts.chart(container, Highcharts.merge(getChartTheme(), {
        chart: {
            // A column per Toronto calendar day: each bar is one discrete measurement, so a line or
            // an area would imply a continuous quantity flowing between days that was never measured.
            type: 'column',
            backgroundColor: 'transparent',
        },
        title: { text: '' },
        xAxis: xAxisOptions,
        yAxis: yAxes,
        legend: { enabled: showCumulative },
        // CHRT-03 forbids getChartTheme() from RETURNING plotOptions (it would trip
        // propsRequireUpdateSeries and wipe the pointer patches on a theme re-apply) — it does NOT
        // forbid a per-chart construction option here, and this chart is always rebuilt from
        // scratch via destroyChart() + Highcharts.chart(), never .update(). PROFILE_COLUMN_MAX_
        // POINT_WIDTH is a CAP, not a fixed width: a well-populated series keeps Highcharts' own
        // responsive sizing, only a sparse series is stopped from expanding to half the plot (D-04).
        plotOptions: { column: { maxPointWidth: PROFILE_COLUMN_MAX_POINT_WIDTH } },
        tooltip: {
            // Single-series, not shared: each bar is one day's own measurement.
            shared: false,
            headerFormat: '',
            // CHRT-03: pointFormatter ONLY — tooltip.formatter is FORBIDDEN (src/js/oura.ts:383).
            pointFormatter: function (this: Highcharts.Point): string {
                const custom = (this as Highcharts.Point & {
                    custom?: { dateLabel?: string; reset?: boolean; spanDays?: number };
                }).custom ?? {};
                const date = custom.dateLabel ?? '';
                const value = formatMetricValue(this.y ?? 0, unit);

                let html = `<b>${escapeHtml(date)}</b><br/>`
                    + `<span style="color:${this.color as string}">●</span> `
                    + `${escapeHtml(this.series.name)}: <b>${escapeHtml(value)}</b>`;

                if (custom.reset === true) {
                    html += `<br/><span>${escapeHtml(RESET_TOOLTIP_COPY)}</span>`;
                }
                if ((custom.spanDays ?? 1) > 1) {
                    // A gap bar carries the WHOLE gain since the last recorded day. Saying so is what
                    // stops it reading as one day's unusually good session.
                    html += `<br/><span>Covers ${custom.spanDays} days since the last snapshot.</span>`;
                }
                return html;
            },
        },
        series,
        credits: { enabled: false },
    } as unknown as Highcharts.Options));
}

function destroyChart(): void {
    if (profileChart) {
        profileChart.destroy();
        profileChart = null;
    }
}

// ---------------------------------------------------------------------------
// DOM plumbing — every element below may be absent on first paint
// ---------------------------------------------------------------------------

/** The "Daily change" section, resolved from the one control the template always renders. */
function chartSection(): HTMLElement | null {
    return document
        .getElementById('mc-profile-metric-select')
        ?.closest<HTMLElement>('.mc-profile-section') ?? null;
}

function ensureChartContainer(): HTMLElement {
    let container = document.getElementById('mc-profile-chart');
    if (!container) {
        container = document.createElement('div');
        container.id = 'mc-profile-chart';
        container.className = 'mc-analytics-chart';
        chartSection()?.appendChild(container);
    }
    container.dataset.uuid = playerUuid;
    container.dataset.metric = currentMetric;
    container.dataset.window = currentWindow;
    container.style.display = '';
    return container;
}

function showEmptyCard(title: string, body: string, reason?: string | null): void {
    const chartEl = document.getElementById('mc-profile-chart');
    if (chartEl) chartEl.style.display = 'none';

    const section = chartSection();
    if (!section) return;

    let card = section.querySelector<HTMLElement>('.chart-empty');
    if (!card) {
        card = document.createElement('div');
        card.className = 'chart chart-empty mc-board-empty';
        const heading = document.createElement('div');
        heading.className = 'chart-empty-title';
        card.appendChild(heading);
        const state = document.createElement('div');
        state.className = 'ksat-empty-state';
        card.appendChild(state);
        section.appendChild(card);
    }
    card.style.display = '';

    const heading = card.querySelector<HTMLElement>('.chart-empty-title');
    if (heading) heading.textContent = title;

    const state = card.querySelector<HTMLElement>('.ksat-empty-state');
    if (state) {
        state.replaceChildren();
        const p = document.createElement('p');
        p.className = 'text-muted mb-0';
        p.textContent = body;
        state.appendChild(p);
        if (reason) {
            // The model's own named reason, never a generic blank. This is what makes D-15 true
            // after an AJAX metric switch and not merely on first paint.
            const why = document.createElement('p');
            why.className = 'text-muted small mb-0 mt-2';
            why.textContent = reason;
            state.appendChild(why);
        }
    }
}

function hideEmptyCard(): void {
    const section = chartSection();
    const card = section?.querySelector<HTMLElement>('.chart-empty');
    if (card) card.style.display = 'none';
}

/**
 * Owns the whole `.mc-board-meta` block: LEAD-05's biggest-day line (server-computed
 * `biggest_day`, no client-side re-derivation) AND the D-06/D-07 inactivity note. The note comes
 * from the pure note-builder in `../utils/profileChartMeta`, which reads `payload.today` — the
 * server's own Toronto calendar day (Plan 246-03) — never the browser clock.
 *
 * The DOM shape below matches `player.latte` §3's server-rendered markup (Plan 246-04 Task 3)
 * exactly: biggest-day span, then a separator span only when both facts are present, then the
 * note span — so the first fetch does not visibly restructure the line under the viewer.
 */
function setChartMetaLine(payload: SeriesPayload): void {
    const section = chartSection();
    if (!section) return;

    let meta = section.querySelector<HTMLElement>('.mc-board-meta');

    const lastPointDate = payload.points.length > 0
        ? payload.points[payload.points.length - 1].date
        : null;
    const note = inactivityNoteText(lastPointDate, payload.today);

    if (payload.biggest_day === null && note === null) {
        if (meta) meta.style.display = 'none';
        return;
    }

    if (!meta) {
        meta = document.createElement('div');
        meta.className = 'mc-board-meta';
        const anchor = section.querySelector('.mc-analytics-selector');
        if (anchor) {
            anchor.insertAdjacentElement('afterend', meta);
        } else {
            section.appendChild(meta);
        }
    }
    meta.style.display = '';
    meta.replaceChildren();

    if (payload.biggest_day !== null) {
        const text = document.createElement('span');
        text.textContent = `Biggest day: ${payload.biggest_day.date} — `
            + formatMetricValue(payload.biggest_day.value, payload.unit ?? 'count');
        meta.appendChild(text);
    }

    if (payload.biggest_day !== null && note !== null) {
        const sep = document.createElement('span');
        sep.className = 'mc-coverage-sep';
        sep.textContent = '·';
        meta.appendChild(sep);
    }

    if (note !== null) {
        const noteSpan = document.createElement('span');
        noteSpan.textContent = note;
        meta.appendChild(noteSpan);
    }
}

/** The optional cumulative overlay toggle. JS-only by nature, so this module creates it. */
function buildCumulativeToggle(): void {
    if (document.getElementById('mc-profile-cumulative')) return;

    const row = document.querySelector('#mc-profile-windows')?.closest('.row');
    if (!row) return;

    const col = document.createElement('div');
    col.className = 'col-12 col-sm-auto';

    const wrap = document.createElement('div');
    wrap.className = 'form-check';

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'form-check-input';
    input.id = 'mc-profile-cumulative';

    const label = document.createElement('label');
    label.className = 'form-check-label small';
    label.htmlFor = 'mc-profile-cumulative';
    label.textContent = 'Show cumulative';

    input.addEventListener('change', () => {
        showCumulative = input.checked;
        // Rebuild from the payload already in hand rather than re-fetching, and rebuild WHOLESALE
        // rather than adding/removing a series, so a repeated toggle cannot leave a stale one behind.
        if (lastPayload !== null && lastPayload.has_data) {
            destroyChart();
            renderProfileChart(lastPayload);
        }
    });

    wrap.appendChild(input);
    wrap.appendChild(label);
    col.appendChild(wrap);
    row.appendChild(col);

    // Hidden until there is actually a chart to overlay.
    col.style.display = 'none';
}

function setCumulativeToggleVisible(visible: boolean): void {
    const col = document.getElementById('mc-profile-cumulative')
        ?.closest<HTMLElement>('.col-12');
    if (col) col.style.display = visible ? '' : 'none';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * The container's data attribute when it exists; the route path otherwise.
 *
 * The fallback is not defensive padding — it is the CURRENT real-world case. Every live player has
 * one snapshot, so has_data is false, so the container carrying data-uuid is not rendered at all.
 */
function resolveUuid(): string {
    const fromContainer = document.getElementById('mc-profile-chart')?.dataset.uuid;
    if (fromContainer) return fromContainer;

    const match = window.location.pathname.match(
        /\/mc\/players\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i,
    );
    return match ? match[1] : '';
}

function readActiveWindow(): string {
    const active = document.querySelector<HTMLElement>(
        '#mc-profile-windows .mc-range-btn.btn-primary[data-window]',
    );
    if (active?.dataset.window) return active.dataset.window;
    const first = document.querySelector<HTMLElement>('#mc-profile-windows .mc-range-btn[data-window]');
    return first?.dataset.window ?? '30d';
}

/** Reads a CSS custom property from the live cascade — the same discipline getChartTheme() uses. */
function readToken(name: string, fallback: string): string {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
}

/**
 * A bare Y-m-d parsed into LOCAL date components.
 *
 * Date.parse('2026-07-08') is specified to read a date-only form as midnight UTC, which in Toronto
 * lands at 20:00 on 2026-07-07 — every bar would render one day early, and the reset marker would
 * sit beside the bar it marks rather than on it. These dates are already Toronto calendar days
 * (D-07); nothing here re-derives the bucket, it only avoids moving it.
 */
function parseLocalDate(date: string): number {
    const parts = date.split('-');
    if (parts.length !== 3) return NaN;
    return new Date(
        Number(parts[0]),
        Number(parts[1]) - 1,
        Number(parts[2]),
    ).getTime();
}

/**
 * Mirrors App\Mc\McMetrics' four unit branches, and therefore leaderboard.ts's formatValue().
 *
 * Duplicated deliberately rather than shared, following this repo's own precedent: player.latte and
 * leaderboard.latte each carry their own copy of the identical unit branch, cross-referenced by
 * comment. The rule is small, stable and already stated in four places; a fifth indirection would
 * buy less than it costs.
 */
function formatMetricValue(value: number, unit: string): string {
    if (unit === 'duration') {
        const whole = Math.floor(Math.max(value, 0));
        return `${Math.floor(whole / 3600)}h ${Math.floor((whole % 3600) / 60)}m`;
    }
    if (unit === 'ratio') {
        return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (unit === 'distance_cm') {
        const km = Math.round((value / 100000) * 10) / 10;
        return `${km.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
    }
    return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/**
 * The tooltip is the one place this module emits markup, because Highcharts' pointFormatter
 * contract is a markup string — there is no textContent equivalent to reach for. Metric labels and
 * series names originate outside this codebase, so they are escaped on the way in rather than
 * trusted (T-228-09-01).
 */
function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
